import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useProteoRisk } from './context';
import {
  REPORT_PATTERN, SERVICE_CODE, isTerminal, jobParameters, listOutputs, loadReportFile, loadSummaryRow, loadUpload,
  openBundleDownload, openOutputDownload, uploadStatus,
} from './api';
import WebserverReportPanel from '../../shared/WebserverReportPanel';
import { StatusBadge, formatDateTime } from './jobs';
import { buildReport } from './report/reportModel';
import ReportView from './report/ReportView';

const POLL_MS = 5000;

const STEPS = [
  { key: 'pending_upload', title: 'Workbook received', text: 'Your workbook and details reach the analysis service.' },
  { key: 'queued', title: 'Waiting in queue', text: 'Your analysis waits for a free worker.' },
  { key: 'running', title: 'Scoring', text: 'Your protein pattern is compared across 129 results.' },
  { key: 'completed', title: 'Results ready', text: 'Your results open on this page.' },
];

function stepIndex(status) {
  const index = STEPS.findIndex((step) => step.key === status);
  return index === -1 ? 0 : index;
}

function StatusView({ status, job, upload }) {
  if (status === 'failed') {
    return (
      <section className="PROTEORISK-section PROTEORISK-status-card failed">
        <h2><i className="fas fa-times-circle" aria-hidden="true" /> This analysis could not be completed</h2>
        <p>Something went wrong while scoring this workbook. The most common cause is a workbook that is not in the expected format.</p>
        {(job?.error_message || upload?.error_message) && (
          <pre className="PROTEORISK-status-error">{job?.error_message || upload?.error_message}</pre>
        )}
        <p>
          Check that the workbook has a sheet named Data with one header row (DAid, barcode, Cohort, Diagnose,
          then one column per protein) and one sample row, then start a new analysis.
        </p>
        <div className="PROTEORISK-status-actions">
          <Link to="/proteorisk" className="PROTEORISK-button primary">
            <i className="fas fa-redo" aria-hidden="true" /> Start a new analysis
          </Link>
          <Link to="/proteorisk/help" className="PROTEORISK-link">Workbook format help</Link>
        </div>
      </section>
    );
  }
  if (status === 'cancelled') {
    return (
      <section className="PROTEORISK-section PROTEORISK-status-card">
        <h2><i className="fas fa-ban" aria-hidden="true" /> This analysis was cancelled</h2>
        <p>No results were made. You can start a new analysis at any time.</p>
        <div className="PROTEORISK-status-actions">
          <Link to="/proteorisk" className="PROTEORISK-button primary">Start a new analysis</Link>
        </div>
      </section>
    );
  }
  const current = stepIndex(status);
  const minutes = Number(job?.est_duration_minutes || 0);
  return (
    <section className="PROTEORISK-section PROTEORISK-status-card">
      <h2><i className="fas fa-hourglass-half" aria-hidden="true" /> Your analysis is on its way</h2>
      <ol className="PROTEORISK-progress-steps">
        {STEPS.map((step, index) => {
          let state = 'todo';
          if (index < current) state = 'done';
          if (index === current) state = 'active';
          return (
            <li key={step.key} className={`PROTEORISK-progress-step ${state}`}>
              <span className="PROTEORISK-progress-marker">
                {state === 'done' && <i className="fas fa-check" aria-hidden="true" />}
                {state === 'active' && <i className="fas fa-spinner fa-spin" aria-hidden="true" />}
                {state === 'todo' && index + 1}
              </span>
              <span className="PROTEORISK-progress-text-block">
                <strong>{step.title}</strong>
                <span>{step.text}</span>
              </span>
            </li>
          );
        })}
      </ol>
      {status === 'pending_upload' ? (
        <p className="PROTEORISK-status-note">
          The workbook upload has not finished. If you closed the page while it was uploading, start a new analysis to upload it again.
        </p>
      ) : (
        <p className="PROTEORISK-status-note">
          {minutes > 0 ? `Scoring usually takes about ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} once it starts. ` : ''}
          This page updates by itself, and you can come back to it later from Your analyses.
          {' '}
          <Link to="/proteorisk/example" className="PROTEORISK-link">See an example report while you wait</Link>
        </p>
      )}
      <dl className="PROTEORISK-status-times">
        {upload?.created_at && <><dt>Submitted</dt><dd>{formatDateTime(upload.created_at)}</dd></>}
        {job?.queued_at && <><dt>Queued</dt><dd>{formatDateTime(job.queued_at)}</dd></>}
        {job?.run_started_at && <><dt>Scoring started</dt><dd>{formatDateTime(job.run_started_at)}</dd></>}
      </dl>
    </section>
  );
}

function useUploadDetail(cookieId, uploadUuid) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!cookieId) return undefined;
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try {
        const body = await loadUpload(cookieId, uploadUuid);
        if (cancelled) return;
        setDetail(body);
        setError('');
        if (!isTerminal(uploadStatus(body).status)) timer = setTimeout(load, POLL_MS);
      } catch (loadError) {
        if (cancelled) return;
        setError(loadError.status === 404
          ? 'This analysis is not linked to this browser.'
          : (loadError.message || 'The analysis could not be loaded.'));
        timer = setTimeout(load, POLL_MS * 2);
      }
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cookieId, uploadUuid]);
  return { detail, error };
}

function useResults(cookieId, uploadUuid, jobUuid, ready) {
  const [result, setResult] = useState({ state: 'idle' });
  useEffect(() => {
    if (!ready || !cookieId || !jobUuid) return undefined;
    let cancelled = false;
    setResult({ state: 'loading' });
    (async () => {
      try {
        const outputs = await listOutputs(cookieId, uploadUuid, jobUuid);
        const report = buildReport(await loadReportFile(cookieId, uploadUuid, jobUuid, outputs));
        let technical;
        try {
          technical = await loadSummaryRow(cookieId, uploadUuid, jobUuid, outputs);
        } catch (summaryError) {
          technical = { error: `Profile spread could not be read: ${summaryError.message}` };
        }
        if (!cancelled) setResult({ state: 'ready', report, technical, outputs });
      } catch (loadError) {
        if (!cancelled) setResult({ state: 'error', error: loadError.message || 'The results could not be loaded.' });
      }
    })();
    return () => { cancelled = true; };
  }, [cookieId, uploadUuid, jobUuid, ready]);
  return result;
}

export default function JobPage() {
  const { uploadUuid } = useParams();
  const { cookieId, jobs, contract } = useProteoRisk();
  const { detail, error } = useUploadDetail(cookieId, uploadUuid);
  const { job, status } = detail ? uploadStatus(detail) : { job: null, status: null };
  const jobUuid = job ? job.uuid : null;
  const result = useResults(cookieId, uploadUuid, jobUuid, status === 'completed');
  const [downloadError, setDownloadError] = useState('');

  const listing = jobs.find((item) => item.upload_uuid === uploadUuid) || null;
  const name = listing ? listing.sample_name : 'ProteoRisk analysis';
  const used = jobParameters(job);
  const entered = listing?.metadata || null;
  const facts = {
    age: used?.age ?? (entered?.age ?? null),
    sex: used?.sex ?? (entered?.sex ?? null),
    bmi: used ? used.bmi : (entered?.bmi ?? null),
  };

  const download = async (action) => {
    setDownloadError('');
    try {
      await action();
    } catch (downloadFailure) {
      setDownloadError(downloadFailure.message || 'The download could not be started.');
    }
  };

  const actions = status === 'completed' && jobUuid ? (
    <>
      <button type="button" className="PROTEORISK-button" onClick={() => download(() => openBundleDownload(cookieId, uploadUuid, jobUuid))}>
        <i className="fas fa-download" aria-hidden="true" /> Download all results
      </button>
      {result.state === 'ready' && (
        <button
          type="button"
          className="PROTEORISK-button subtle"
          onClick={() => download(() => openOutputDownload(cookieId, uploadUuid, jobUuid, result.outputs, REPORT_PATTERN))}
        >
          <i className="fas fa-file-code" aria-hidden="true" /> Report file
        </button>
      )}
    </>
  ) : null;

  let body = null;
  if (!detail && !error) {
    body = <div className="PROTEORISK-section PROTEORISK-loading"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading the analysis...</div>;
  } else if (!detail && error) {
    body = (
      <section className="PROTEORISK-section PROTEORISK-status-card failed">
        <h2><i className="fas fa-exclamation-circle" aria-hidden="true" /> {error}</h2>
        <p>Analyses are linked to the anonymous ID in the browser that started them.</p>
        <div className="PROTEORISK-status-actions">
          <Link to="/proteorisk" className="PROTEORISK-button primary">Your analyses</Link>
        </div>
      </section>
    );
  } else if (status !== 'completed') {
    body = <StatusView status={status} job={job} upload={detail.upload} />;
  } else if (result.state === 'error') {
    body = (
      <section className="PROTEORISK-section PROTEORISK-status-card failed">
        <h2><i className="fas fa-exclamation-circle" aria-hidden="true" /> The results could not be shown</h2>
        <p>{result.error}</p>
        <p>You can still download every file the analysis produced.</p>
        <div className="PROTEORISK-status-actions">{actions}</div>
      </section>
    );
  } else if (result.state !== 'ready') {
    body = <div className="PROTEORISK-section PROTEORISK-loading"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading your results...</div>;
  } else {
    body = <ReportView report={result.report} facts={facts} technical={result.technical} actions={actions} />;
  }

  return (
    <div className="PROTEORISK-page PROTEORISK-job-page">
      <div className="PROTEORISK-job-header">
        <Link to="/proteorisk" className="PROTEORISK-back">
          <i className="fas fa-arrow-left" aria-hidden="true" /> Your analyses
        </Link>
        <div className="PROTEORISK-job-header-main">
          <div>
            <h1>{name}</h1>
            <p>
              {[
                detail?.upload?.created_at ? `Submitted ${formatDateTime(detail.upload.created_at)}` : null,
                job ? `${job.display_name || job.pipeline} ${job.code_version || ''}`.trim() : null,
              ].filter(Boolean).join(' · ')}
            </p>
          </div>
          {status && <StatusBadge status={status} />}
        </div>
        {error && detail && <p className="PROTEORISK-inline-warning">{`Could not refresh: ${error}`}</p>}
        {downloadError && <p className="PROTEORISK-inline-warning">{downloadError}</p>}
      </div>
      {body}
      {status === 'completed' && jobUuid && (
        <div className="PROTEORISK-section">
          <WebserverReportPanel
            serviceCode={SERVICE_CODE}
            token={cookieId}
            job={{ upload_uuid: uploadUuid, job_uuid: jobUuid, status }}
            reportTypes={contract?.pipeline?.report_types || []}
          />
        </div>
      )}
    </div>
  );
}
